package top.wcpe.allincore.ui.runtime.crossplatform

import top.wcpe.allincore.dsl.ir.crossplatform.CanonicalDrawRecord
import top.wcpe.allincore.dsl.ir.crossplatform.CanonicalField
import top.wcpe.allincore.dsl.ir.crossplatform.CanonicalFloat
import top.wcpe.allincore.dsl.ir.crossplatform.CanonicalLayoutRecord
import top.wcpe.allincore.dsl.ir.crossplatform.CanonicalRect
import top.wcpe.allincore.dsl.ir.crossplatform.CanonicalValue
import top.wcpe.allincore.dsl.ir.crossplatform.CrossPlatformContractValidator
import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.layer.DrawRecord
import top.wcpe.allincore.ui.runtime.render.layer.DrawTraceEntry
import top.wcpe.allincore.ui.runtime.render.layer.LayerBufferingBridge
import top.wcpe.allincore.ui.runtime.render.layer.StateSnapshot
import top.wcpe.allincore.ui.runtime.render.layer.TransformEntry
import top.wcpe.allincore.ui.runtime.render.model.ImageStyle
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.ShadowParams
import top.wcpe.allincore.ui.runtime.render.model.StyledFill

/** 把运行时布局与绘制记录投影为跨平台冻结契约。 */
@Suppress("TooManyFunctions")
object CrossPlatformRecordCanonicalizer {

    fun canonicalizeLayout(root: UIElement<*>, layout: LayoutResult): List<CanonicalLayoutRecord> {
        val records = RuntimeNodePaths.collect(root).entries.mapIndexed { index, (element, nodePath) ->
            val box = layout.boxOf(element)
            CanonicalLayoutRecord(
                nodePath,
                index,
                box?.let { CanonicalRect(num(it.x()), num(it.y()), num(it.width()), num(it.height())) },
            )
        }
        requireValidLayout(records)
        return records
    }

    fun canonicalizeDraw(entries: List<DrawTraceEntry>): List<CanonicalDrawRecord> {
        val records = entries.map(::canonicalizeDrawEntry)
        requireValidDraw(records)
        return records
    }

    private fun canonicalizeDrawEntry(entry: DrawTraceEntry): CanonicalDrawRecord {
        val record = entry.record
        val type = requiredType(record)
        return CanonicalDrawRecord(
            entry.phase.name,
            entry.layerId,
            entry.sequence,
            requireNotNull(record.nodePath()) { "DrawRecord 缺少 nodePath" },
            type.name,
            state(record.state()),
            drawFields(record),
        )
    }

    private fun drawFields(record: DrawRecord): CanonicalValue.ObjectValue = obj(
        basicFields(record) +
            itemAndEntityFields(record) +
            modelFields(record) +
            effectFields(record) +
            nestedFields(record),
    )

    private fun basicFields(record: DrawRecord): List<Pair<String, CanonicalValue>> = listOf(
        "argb" to int(record.argb()),
        "bold" to bool(record.bold()),
        "command" to renderCommand(record.command()),
        "fontName" to text(record.fontName()),
        "h" to number(record.h()),
        "imageStyle" to imageStyle(record.imageStyle()),
        "italic" to bool(record.italic()),
        "lineThickness" to number(record.lineThickness()),
        "scale" to number(record.scale()),
        "sequence" to int(record.sequence()),
        "shadow" to bool(record.shadow()),
        "shadowParams" to shadowParams(record.shadowParams()),
        "styledFill" to styledFill(record.styledFill()),
        "text" to text(record.text()),
        "texture" to text(record.texture()),
        "w" to number(record.w()),
        "x" to number(record.x()),
        "y" to number(record.y()),
    )

    private fun itemAndEntityFields(record: DrawRecord): List<Pair<String, CanonicalValue>> = listOf(
        "backdropColor" to int(record.backdropColor()),
        "countAnchor" to text(record.countAnchor()),
        "countOffsetX" to number(record.countOffsetX()),
        "countOffsetY" to number(record.countOffsetY()),
        "enchanted" to bool(record.enchanted()),
        "entityName" to text(record.entityName()),
        "entityRef" to text(record.entityRef()),
        "entityScale" to number(record.entityScale()),
        "entityType" to text(record.entityType()),
        "followMouse" to bool(record.followMouse()),
        "itemCount" to int(record.itemCount()),
        "itemId" to text(record.itemId()),
        "itemNbt" to text(record.itemNbt()),
        "mouseX" to number(record.mouseX()),
        "mouseY" to number(record.mouseY()),
        "rotationX" to number(record.rotationX()),
        "rotationY" to number(record.rotationY()),
        "showCount" to bool(record.showCount()),
    )

    private fun modelFields(record: DrawRecord): List<Pair<String, CanonicalValue>> = listOf(
        "modelBackgroundColor" to int(record.modelBackgroundColor()),
        "modelBase" to text(record.modelBase()),
        "modelInstanceKey" to text(record.modelInstanceKey()),
        "modelInteractive" to bool(record.modelInteractive()),
        "modelRotationX" to number(record.modelRotationX()),
        "modelRotationY" to number(record.modelRotationY()),
        "modelScale" to number(record.modelScale()),
        "modelSource" to text(record.modelSource()),
        "modelTexture" to text(record.modelTexture()),
    )

    private fun effectFields(record: DrawRecord): List<Pair<String, CanonicalValue>> = listOf(
        "effectAnimated" to bool(record.effectAnimated()),
        "effectCenterX" to number(record.effectCenterX()),
        "effectCenterY" to number(record.effectCenterY()),
        "effectColorA" to int(record.effectColorA()),
        "effectColorB" to int(record.effectColorB()),
        "effectCornerRadii" to numbers(record.effectCornerRadii()),
        "effectDensity" to number(record.effectDensity()),
        "effectFrequency" to number(record.effectFrequency()),
        "effectIntensity" to number(record.effectIntensity()),
        "effectKind" to text(record.effectKind()),
        "effectMode" to int(record.effectMode()),
        "effectProgress" to number(record.effectProgress()),
        "effectRadius" to number(record.effectRadius()),
        "effectSize" to number(record.effectSize()),
        "effectSpeed" to number(record.effectSpeed()),
    )

    private fun nestedFields(record: DrawRecord): List<Pair<String, CanonicalValue>> = listOf(
        "backdropBlurRadius" to number(record.backdropBlurRadius()),
        "batchCommands" to records(record.batchCommands()),
        "blendGroupCommands" to records(record.blendGroupCommands()),
        "blendMode" to int(record.blendMode()),
        "blurRadius" to number(record.blurRadius()),
        "filterGroupCommands" to records(record.filterGroupCommands()),
        "richTextLineStep" to number(record.richTextLineStep()),
        "richTextMaxWidth" to number(record.richTextMaxWidth()),
        "richTextMultiline" to bool(record.richTextMultiline()),
        "uvH" to number(record.uvH()),
        "uvW" to number(record.uvW()),
        "uvX" to number(record.uvX()),
        "uvY" to number(record.uvY()),
    )

    private fun state(snapshot: StateSnapshot?): CanonicalValue.ObjectValue {
        val value = snapshot ?: StateSnapshot.EMPTY
        return obj(
            "alpha" to numbers(value.alphaStack),
            "clip" to numbers(value.currentClip),
            "colorMatrix" to list(value.colorMatrixStack.map(::numbers)),
            "transform" to list(value.transformStack.map(::transform)),
        )
    }

    private fun transform(entry: TransformEntry): CanonicalValue = when (entry) {
        is TransformEntry.TranslateScale -> obj(
            "sx" to number(entry.sx),
            "sy" to number(entry.sy),
            "tx" to number(entry.tx),
            "ty" to number(entry.ty),
            "type" to text("TRANSLATE_SCALE"),
        )
        is TransformEntry.Matrix3x3 -> obj(
            "matrix" to numbers(entry.mat),
            "type" to text("MATRIX_3X3"),
        )
        is TransformEntry.Matrix4x4 -> obj(
            "matrix" to numbers(entry.mat),
            "type" to text("MATRIX_4X4"),
        )
    }

    private fun records(values: List<DrawRecord>?): CanonicalValue =
        values?.let { list(it.map(::nestedRecord)) } ?: CanonicalValue.Null

    private fun nestedRecord(record: DrawRecord): CanonicalValue = obj(
        "commandType" to text(requiredType(record).name),
        "fields" to drawFields(record),
        "nodePath" to text(requireNotNull(record.nodePath()) { "嵌套 DrawRecord 缺少 nodePath" }),
        "sequence" to int(record.sequence()),
        "state" to state(record.state()),
    )

    private fun renderCommand(command: RenderCommand?): CanonicalValue {
        if (command == null) return CanonicalValue.Null
        requireKnownType(command.type)
        return obj(
            "argb" to int(command.argb),
            "clipId" to int(command.clipId),
            "entityRef" to text(command.entityRef),
            "h" to number(command.h),
            "imageStyle" to imageStyle(command.imageStyle),
            "itemCount" to int(command.itemCount),
            "itemId" to text(command.itemId),
            "itemNbt" to text(command.itemNbt),
            "mouseX" to number(command.mouseX),
            "mouseY" to number(command.mouseY),
            "scale" to number(command.scale),
            "shadow" to shadowParams(command.shadow),
            "styledFill" to styledFill(command.styledFill),
            "text" to text(command.text),
            "texture" to text(command.texture),
            "type" to text(command.type.name),
            "uvH" to number(command.uvH),
            "uvW" to number(command.uvW),
            "uvX" to number(command.uvX),
            "uvY" to number(command.uvY),
            "w" to number(command.w),
            "x" to number(command.x),
            "y" to number(command.y),
        )
    }

    private fun styledFill(value: StyledFill?): CanonicalValue {
        if (value == null) return CanonicalValue.Null
        return obj(
            "borderColor" to int(value.borderColor),
            "borderWidth" to number(value.borderWidth),
            "cornerRadiusBL" to number(value.cornerRadiusBL),
            "cornerRadiusBR" to number(value.cornerRadiusBR),
            "cornerRadiusTL" to number(value.cornerRadiusTL),
            "cornerRadiusTR" to number(value.cornerRadiusTR),
            "gradientAngleRadians" to number(value.gradientAngleRadians),
            "gradientStops" to list(value.gradientStops.map(::gradientStop)),
            "gradientType" to text(value.gradientType.name),
            "maskParam1" to number(value.maskParam1),
            "maskParam2" to number(value.maskParam2),
            "maskParam3" to number(value.maskParam3),
            "maskType" to text(value.maskType.name),
            "radialCenterX" to number(value.radialCenterX),
            "radialCenterY" to number(value.radialCenterY),
            "radialRadius" to number(value.radialRadius),
        )
    }

    private fun gradientStop(value: top.wcpe.allincore.ui.runtime.render.model.GradientStop): CanonicalValue = obj(
        "argb" to int(value.argb),
        "offset" to number(value.offset),
    )

    private fun shadowParams(value: ShadowParams?): CanonicalValue {
        if (value == null) return CanonicalValue.Null
        return obj(
            "blur" to number(value.blur),
            "color" to int(value.color),
            "inset" to bool(value.inset),
            "offsetX" to number(value.offsetX),
            "offsetY" to number(value.offsetY),
            "spread" to number(value.spread),
        )
    }

    private fun imageStyle(value: ImageStyle?): CanonicalValue {
        if (value == null) return CanonicalValue.Null
        return obj(
            "mode" to text(value.mode.name),
            "uvOffsetX" to number(value.uvOffsetX),
            "uvOffsetY" to number(value.uvOffsetY),
            "uvRotation" to number(value.uvRotation),
            "uvScaleX" to number(value.uvScaleX),
            "uvScaleY" to number(value.uvScaleY),
        )
    }

    private fun requiredType(record: DrawRecord): RenderCommandType {
        val type = requireNotNull(record.type()) { "DrawRecord 缺少 type" }
        requireKnownType(type)
        return type
    }

    private fun requireKnownType(type: RenderCommandType) {
        when (type) {
            RenderCommandType.FILL_RECT,
            RenderCommandType.DRAW_TEXT,
            RenderCommandType.DRAW_RICH_TEXT,
            RenderCommandType.DRAW_LINE,
            RenderCommandType.DRAW_IMAGE,
            RenderCommandType.ROUNDED_RECT,
            RenderCommandType.GRADIENT,
            RenderCommandType.SHADOW,
            RenderCommandType.IMAGE_STYLED,
            RenderCommandType.DRAW_ITEM_STACK,
            RenderCommandType.DRAW_TEXTURED_QUAD,
            RenderCommandType.DRAW_ENTITY_PREVIEW,
            RenderCommandType.NEON_GLOW,
            RenderCommandType.RIPPLE,
            RenderCommandType.PROCEDURAL_TEXTURE,
            RenderCommandType.MATERIAL_LAYER,
            RenderCommandType.FLOW,
            RenderCommandType.ENERGY,
            RenderCommandType.FILTER_GROUP,
            RenderCommandType.BACKDROP_FILTER,
            RenderCommandType.BLEND_GROUP,
            RenderCommandType.HISTORY_GROUP,
            RenderCommandType.PARTICLE,
            RenderCommandType.DRAW_MODEL_PREVIEW,
            -> Unit
        }
    }

    private fun requireValidLayout(records: List<CanonicalLayoutRecord>) {
        val errors = CrossPlatformContractValidator.validateLayoutRecords(records)
        require(errors.isEmpty()) { "布局规范记录校验失败：${errors.joinToString("；")}" }
    }

    private fun requireValidDraw(records: List<CanonicalDrawRecord>) {
        val errors = CrossPlatformContractValidator.validateDrawRecords(records)
        require(errors.isEmpty()) { "绘制规范记录校验失败：${errors.joinToString("；")}" }
    }

    private fun obj(vararg fields: Pair<String, CanonicalValue>): CanonicalValue.ObjectValue = obj(fields.asList())

    private fun obj(fields: List<Pair<String, CanonicalValue>>): CanonicalValue.ObjectValue =
        CanonicalValue.ObjectValue(fields.sortedBy { it.first }.map { CanonicalField(it.first, it.second) })

    private fun list(values: List<CanonicalValue>): CanonicalValue = CanonicalValue.ListValue(values)
    private fun bool(value: Boolean): CanonicalValue = CanonicalValue.Bool(value)
    private fun int(value: Int): CanonicalValue = CanonicalValue.IntValue(value.toLong())
    private fun text(value: String?): CanonicalValue = value?.let(CanonicalValue::Text) ?: CanonicalValue.Null
    private fun number(value: Float): CanonicalValue = CanonicalValue.Number(num(value))
    private fun numbers(values: List<Float>): CanonicalValue = list(values.map(::number))
    private fun numbers(values: FloatArray?): CanonicalValue = values?.let { list(it.map(::number)) } ?: CanonicalValue.Null
    private fun num(value: Float): CanonicalFloat = CanonicalFloat.fromFloat(value)
}

/** 为 IR 与手工元素树生成统一、确定性的节点路径表。 */
internal object RuntimeNodePaths {
    fun collect(root: UIElement<*>): Map<UIElement<*>, String> {
        val paths = linkedMapOf<UIElement<*>, String>()
        collect(root, MANUAL_ROOT, paths)
        return paths
    }

    inline fun <T> withCapture(bridge: UiRenderBridge, nodePath: String?, block: () -> T): T {
        val capture = bridge as? LayerBufferingBridge ?: return block()
        if (!capture.capturesRuntimeNodePath()) return block()
        val previous = capture.currentRuntimeNodePath()
        capture.updateRuntimeNodePath(requireNotNull(nodePath) { "元素缺少运行时节点路径" })
        return try {
            block()
        } finally {
            capture.updateRuntimeNodePath(previous)
        }
    }

    private fun collect(
        element: UIElement<*>,
        fallbackPath: String,
        paths: MutableMap<UIElement<*>, String>,
    ) {
        val nodePath = element.runtimeNodePath() ?: fallbackPath
        paths[element] = nodePath
        element.children().toList().forEachIndexed { index, child ->
            collect(child, "$nodePath/$index", paths)
        }
    }

    private const val MANUAL_ROOT = "manual/0"
}
